import { useCallback, useEffect, useRef, useState } from 'react';
import { engine } from './audio/engine';
import { ChopView } from './components/ChopView';
import { ExportDialog } from './components/ExportDialog';
import { LoopFinder } from './components/LoopFinder';
import { MixView } from './components/MixView';
import { SequenceView } from './components/SequenceView';
import { Shortcuts } from './components/Shortcuts';
import { Tutorial, tourWasSeen } from './components/Tutorial';
import { SongView } from './components/SongView';
import { SourceLibrary } from './components/SourceLibrary';
import { TopBar } from './components/TopBar';
import { TrackStrip } from './components/TrackStrip';
import { Logo } from './components/Logo';
import { Splash, markSplashSeen, splashWasSeen } from './components/Splash';
import { loadSession, saveSession } from './store/session';
import { patternOf, useStore, type View } from './store/useStore';

const VIEW_ORDER: View[] = ['chop', 'sequence', 'song', 'mix'];

export default function App() {
  const view = useStore((s) => s.view);
  const toast = useStore((s) => s.toast);
  const [showExport, setShowExport] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showFinder, setShowFinder] = useState(false);

  /**
   * The loop finder's preview shares the master bus with the sequencer, so four
   * tracks running over the top of it make auditioning a candidate pointless.
   * Silence the transport while it's open and put it back on the way out.
   *
   * Done here rather than in an effect inside the finder: StrictMode invokes
   * mount effects twice, and the cleanup in between was restarting playback the
   * moment it had been stopped.
   */
  const resumeAfterFinder = useRef(false);
  const openFinder = useCallback(() => {
    resumeAfterFinder.current = engine.isPlaying;
    if (resumeAfterFinder.current) engine.stop();
    setShowFinder(true);
  }, []);
  const closeFinder = useCallback(() => {
    setShowFinder(false);
    if (!resumeAfterFinder.current) return;
    resumeAfterFinder.current = false;
    const s = useStore.getState();
    void engine.start(s.project, s.playMode, 0);
  }, []);
  const [showTour, setShowTour] = useState(false);
  const [booting, setBooting] = useState(true);
  /** Decided once, on mount, so a re-render can't resurrect it mid-fade. */
  const [showSplash] = useState(() => !splashWasSeen());
  const [splashDone, setSplashDone] = useState(!showSplash);

  // Marked as soon as it's shown rather than when it finishes, so a reload
  // during the splash doesn't bring it straight back.
  useEffect(() => {
    if (showSplash) markSplashSeen();
  }, [showSplash]);

  // The tour waits for both — it can't point at UI that's still behind a cover.
  useEffect(() => {
    if (!booting && splashDone && !tourWasSeen()) setShowTour(true);
  }, [booting, splashDone]);

  /**
   * Restore the last session before showing anything. Rendering the empty
   * default project first and swapping it a moment later reads as data loss,
   * even though it isn't.
   */
  useEffect(() => {
    let cancelled = false;
    void loadSession()
      .then((session) => {
        if (cancelled || !session) return;
        const store = useStore.getState();
        store.loadProject(session.project);
        if (session.ui) store.setUi(session.ui);
        if (session.lost.length) {
          store.notify(`Couldn't restore audio for ${session.lost.join(', ')}`);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (cancelled) return;
        setBooting(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Autosave, debounced. Encoding is cached per source, so a save that follows
   * a knob tweak is just the JSON.
   */
  useEffect(() => {
    if (booting) return;
    let timer: number | undefined;
    let failed = false;
    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const s = useStore.getState();
        void saveSession(s.project, {
          view: s.view,
          selectedTrackId: s.selectedTrackId,
          selectedSourceId: s.selectedSourceId,
          playMode: s.playMode,
        }).catch(() => {
          if (failed) return;
          failed = true;
          useStore.getState().notify('Session too large to save automatically');
        });
      }, 1200);
    };
    schedule();
    const unsub = useStore.subscribe((s, prev) => {
      if (s.project !== prev.project || s.view !== prev.view) schedule();
    });
    return () => {
      unsub();
      window.clearTimeout(timer);
    };
  }, [booting]);

  // Keep the engine's copy of the project in step with the store.
  useEffect(() => {
    engine.update(useStore.getState().project);
    return useStore.subscribe((s, prev) => {
      if (s.project !== prev.project) engine.update(s.project);
    });
  }, []);

  useEffect(() => engine.subscribe({ onStateChange: (p) => useStore.setState({ playing: p }) }), []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => useStore.getState().notify(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        return;
      }
      // A modal owns the keyboard while it is open — otherwise space would
      // start the transport underneath the loop finder's own preview.
      if (document.querySelector('[data-modal-open]')) return;
      const store = useStore.getState();
      const { project, selectedSteps } = store;
      const track =
        project.tracks.find((t) => t.id === store.selectedTrackId) ?? project.tracks[0] ?? null;
      const pattern = patternOf(project, track);
      const mod = e.metaKey || e.ctrlKey;

      if (e.code === 'Space') {
        e.preventDefault();
        if (store.playing) {
          engine.stop();
        } else {
          void engine.start(project, store.playMode, 0);
        }
        return;
      }
      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        e.shiftKey ? store.redo() : store.undo();
        return;
      }
      if (e.key === '[' || e.key === ']') {
        const i = VIEW_ORDER.indexOf(store.view);
        const next = VIEW_ORDER[(i + (e.key === ']' ? 1 : VIEW_ORDER.length - 1)) % VIEW_ORDER.length];
        store.setUi({ view: next });
        return;
      }
      if (/^[1-8]$/.test(e.key)) {
        const t = project.tracks[Number(e.key) - 1];
        if (t) store.setUi({ selectedTrackId: t.id, selectedSteps: [] });
        return;
      }
      if (!pattern || !selectedSteps.length) return;

      const steps = pattern.steps;
      const idx = steps.findIndex((s) => s.id === selectedSteps[0]);
      const first = steps[idx];
      if (!first) return;

      switch (e.key.toLowerCase()) {
        case 'r':
          store.updateSteps(pattern.id, selectedSteps, { reverse: !first.reverse });
          break;
        case 'm':
          store.updateSteps(pattern.id, selectedSteps, { mute: !first.mute });
          break;
        case 'x':
          store.updateSteps(pattern.id, selectedSteps, { slice: -1 });
          break;
        case 'd':
          store.duplicateStep(pattern.id, idx);
          break;
        case 'backspace':
        case 'delete':
          e.preventDefault();
          store.deleteStep(pattern.id, idx);
          store.setUi({ selectedSteps: [] });
          break;
        case 'arrowleft':
          e.preventDefault();
          store.moveStep(pattern.id, idx, idx - 1);
          break;
        case 'arrowright':
          e.preventDefault();
          store.moveStep(pattern.id, idx, idx + 1);
          break;
        case 'arrowup':
          e.preventDefault();
          if (e.shiftKey) {
            store.updateSteps(pattern.id, selectedSteps, { pitch: first.pitch + 1 });
          } else {
            store.updateSteps(pattern.id, selectedSteps, { slice: first.slice + 1 });
          }
          break;
        case 'arrowdown':
          e.preventDefault();
          if (e.shiftKey) {
            store.updateSteps(pattern.id, selectedSteps, { pitch: first.pitch - 1 });
          } else {
            store.updateSteps(pattern.id, selectedSteps, { slice: Math.max(-1, first.slice - 1) });
          }
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="flex h-full flex-col bg-ink-950">
      {!splashDone && <Splash ready={!booting} onDone={() => setSplashDone(true)} />}

      {/* Without the splash there's still a moment of restore to cover, or the
          empty default project shows and is replaced — which reads as data loss. */}
      {splashDone && booting && (
        <div className="fade-in fixed inset-0 z-[70] flex items-center justify-center bg-ink-950">
          <div className="flex items-center gap-2.5 text-ink-400">
            <Logo size={24} />
            <span className="display text-sm">Happy Chopper</span>
          </div>
        </div>
      )}

      <TopBar
        onExport={() => setShowExport(true)}
        onShortcuts={() => setShowShortcuts(true)}
        onTour={() => setShowTour(true)}
      />

      <div className="flex min-h-0 flex-1">
        <SourceLibrary onFindLoops={openFinder} />
        <main className="min-w-0 flex-1 overflow-hidden">
          {view === 'chop' && <ChopView />}
          {view === 'sequence' && <SequenceView />}
          {view === 'song' && <SongView />}
          {view === 'mix' && <MixView />}
        </main>
      </div>

      <TrackStrip />

      {showTour && <Tutorial onClose={() => setShowTour(false)} />}
      {showFinder && <LoopFinder onClose={closeFinder} />}
      {showExport && <ExportDialog onClose={() => setShowExport(false)} />}
      {showShortcuts && <Shortcuts onClose={() => setShowShortcuts(false)} />}

      {toast && (
        <div className="rise pointer-events-none fixed bottom-28 left-1/2 z-50 -translate-x-1/2 rounded-lg border border-volt-500/40 bg-ink-850/95 px-3.5 py-2 text-[11px] text-volt-400 shadow-lg backdrop-blur">
          {toast}
        </div>
      )}
    </div>
  );
}
